import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Paper,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  IconButton,
  Box,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Tooltip,
  Divider,
  FormGroup,
  Chip,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCart } from '../../../../CartContext';
import { Breadcrumb } from 'components';
import {
  getAllContainers,
  addDraftCheckOutItem,
  getQualityAndPackingItems,
  standardQuoteSearch,
  searchTestResult,
  filterContainerBasedOnTest
} from 'services';
import { useLocation } from 'react-router';
import DemoModal from './Components/DemoModal';

interface ClientOption {
  id: number;
  name: string;
  code?: string;
  buCode?: string;
}

const formatClientOption = (client: ClientOption | string | null | undefined) => {
  if (typeof client === 'string') {
    return client;
  }

  if (!client) {
    return '';
  }

  return [client.name, client.code, client.buCode].filter(Boolean).join(' - ');
};

const COLOR_NAME_TO_HEX: { [key: string]: string } = {
  'red': '#ff0000',
  'blue': '#0000ff',
  'darkblue': '#00008b',
  'dark blue': '#00008b',
  'lightblue': '#add8e6',
  'light blue': '#add8e6',
  'green': '#008000',
  'yellow': '#ffff00',
  'yellow cap': '#ffff00',
  'orange': '#ffa500',
  'purple': '#800080',
  'pink': '#ffc0cb',
  'black': '#000000',
  'white': '#ffffff',
  'white cap': '#ffffff',
  'gray': '#808080',
  'grey': '#808080',
  'grey cap': '#808080',
  'gray cap': '#808080',
  'silver': '#c0c0c0',
  'clear': '#ffffff',
  'various': '#ffffff',
  'pvc': '#ffffff',
  'mauve': '#f5f5dc'
};

const BOTTLE_IMAGE_MAP: Record<number, string | undefined> = {
  1: require('assets/container_images/BAG_4-05.jpg'),
  2: require('assets/container_images/BAG_3-2.jpg'),
  3: require('assets/container_images/BAG_3-3.jpg'),
  4: require('assets/container_images/BAG_4-08.jpg'),
  5: require('assets/container_images/BAG_4-02.jpg'),
  6: undefined,
  7: require('assets/container_images/BAG_1-4.jpg'),
  8: require('assets/container_images/BAG_1-3.jpg'),
  9: require('assets/container_images/BAG_4-10.jpg'),
  10: require('assets/container_images/BAG_4-17.jpg'),
  11: require('assets/container_images/BAG_2-3.jpg'),
  12: require('assets/container_images/BAG_1-1.jpg'),
  13: require('assets/container_images/BAG_2-5.jpg'),
  14: require('assets/container_images/BAG_2-4.jpg'),
  15: require('assets/container_images/BAG_4-03.jpg'),
  16: require('assets/container_images/BAG_4-06.jpg'),
  17: require('assets/container_images/BAG_4-15.jpg'),
  18: require('assets/container_images/BAG_4-16.jpg'),
  19: require('assets/container_images/BAG_4-07.jpg'),
  20: require('assets/container_images/BAG_1-5.jpg'),
  21: undefined,
  22: require('assets/container_images/BAG_2-1.jpg'),
  23: undefined,
  24: require('assets/container_images/BAG_4-03.jpg'),
  25: require('assets/container_images/BAG_4-10.jpg'),
  26: require('assets/container_images/BAG_4-17.jpg'),
  27: undefined,
  28: undefined,
  29: require('assets/container_images/BAG_3-1.jpg'),
  30: undefined,
  31: undefined,
  32: undefined,
  33: undefined,
  34: require('assets/container_images/BAG_3-6.jpg'),
  35: undefined,
  36: require('assets/container_images/BAG_4-04.jpg'),
  37: require('assets/container_images/BAG_4-09.jpg'),
  38: require('assets/container_images/BAG_2-2.jpg'),
};

const QUALITY_IMAGE_MAP: Record<number, string | undefined> = {
  1: undefined,
  2: require('assets/quality_packing_images/2.jpg'),
  3: require('assets/quality_packing_images/3.png'),
  4: require('assets/quality_packing_images/4.jpg'),
  5: require('assets/quality_packing_images/5.jpg'),
  6: require('assets/quality_packing_images/6.jpg'),
  7: require('assets/quality_packing_images/7.jpg'),
  8: require('assets/quality_packing_images/8.jpg'),
  9: undefined,
  10: undefined,
  11: require('assets/quality_packing_images/11.png'),
  12: require('assets/quality_packing_images/12.png'),
  13: require('assets/quality_packing_images/13.png'),
  14: require('assets/quality_packing_images/14.jpg'),
  15: require('assets/quality_packing_images/15.jpeg'),
  16: require('assets/quality_packing_images/16.jpg'),
  17: require('assets/quality_packing_images/17.jpg'),
};

const MATRIX_BUTTONS = [
  { label: 'SOLID', id: 1 },
  { label: 'LIQUID', id: 2 },
  { label: 'AIR', id: 3 }
];

const getContrastColor = (bgColor: string) => {
  const color = bgColor.charAt(0) === '#' ? bgColor.substring(1) : bgColor;
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#222' : '#fff';
};

const colorNameToHex = (color: string) => COLOR_NAME_TO_HEX[color.trim().toLowerCase()] || color;

const getBulkUnitType = (item: any) => `${item?.bulkType ?? ''}`.trim();

const getUnitTypeOptions = (item: any) => {
  const bulkUnitType = getBulkUnitType(item);
  return bulkUnitType && bulkUnitType !== 'Individual'
    ? ['Individual', bulkUnitType]
    : ['Individual'];
};

interface ContainerItemCardProps {
  bottle: any;
  selected: boolean;
  quantity: number | "";
  unitType: string;
  multiplier: number;
  imageHeight: number;
  isClientSelectionRequired: boolean;
  showClientTooltip: boolean;
  onToggleSelected: (item: any) => void;
  onOpenImagePreview: (imageSrc: string | undefined, title: string) => void;
  onViewDetails: (item: any, quantity: number | "") => void;
  onQuantityChange: (id: number, value: number | "") => void;
  onQuantityBlur: (id: number, value: string) => void;
  onUnitTypeChange: (item: any, value: string) => void;
  onMultiplierChange: (id: number, value: number) => void;
  onAddToCart: (item: any) => void;
}

const ContainerItemCard = memo(({
  bottle,
  selected,
  quantity,
  unitType,
  multiplier,
  imageHeight,
  isClientSelectionRequired,
  showClientTooltip,
  onToggleSelected,
  onOpenImagePreview,
  onViewDetails,
  onQuantityChange,
  onQuantityBlur,
  onUnitTypeChange,
  onMultiplierChange,
  onAddToCart
}: ContainerItemCardProps) => {
  const imageSrc = bottle?.id ? BOTTLE_IMAGE_MAP[bottle.id] : undefined;
  const hasImage = !!imageSrc;
  const isDisabled = unitType?.toLowerCase() !== 'individual';

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          border: selected ? '2px solid #003883' : '1px solid #e0e0e0',
          borderRadius: 2,
          transition: 'border-color 0.15s',
          '&:hover': {
            borderColor: '#003883',
          }
        }}
      >
        <Box
          onClick={() => onToggleSelected(bottle)}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: imageHeight,
            flex: `0 0 ${imageHeight}px`,
            backgroundColor: '#f5f8fc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover .zoom-btn': { opacity: 1, pointerEvents: 'auto' }
          }}
        >
          {selected && (
            <Box
              onClick={(event) => { event.stopPropagation(); onToggleSelected(bottle); }}
              role="checkbox"
              aria-checked={true}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#003883',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ color: '#fff', fontSize: '12px', fontWeight: 700, lineHeight: 1 }}>✓</Box>
            </Box>
          )}
          {hasImage ? (
            <CardMedia
              component="img"
              height={imageHeight}
              loading="lazy"
              image={imageSrc}
              alt={bottle.name}
              sx={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'center center',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 3,
                gap: 1.5
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: '#003883',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: 1.3
                }}
              >
                {bottle.name}
              </Typography>
            </Box>
          )}
          {hasImage && (
            <IconButton
              className="zoom-btn"
              onClick={(event) => {
                event.stopPropagation();
                onOpenImagePreview(imageSrc, bottle.name);
              }}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255,255,255,0.85)',
                opacity: 0,
                pointerEvents: 'none'
              }}
              aria-label="Zoom image"
            >
              <ZoomInIcon />
            </IconButton>
          )}
        </Box>
        <CardContent sx={{ textAlign: 'left', p: 2, pb: 1 }}>
          {/* Bottle Name with Tooltip */}
          <Tooltip title={bottle.analysisType || ''} arrow placement="top">
            <Typography
              variant="h6"
              sx={{
                my: 1,
                mb: 2,
                fontSize: '16px',
                lineHeight: 1.4,
                fontWeight: 600,
                color: '#003883',
                minHeight: 48,
              }}
            >
              {bottle.name}
            </Typography>
          </Tooltip>

          {/* Size & Color */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mt: 1,
            }}
          >
            <Typography variant="body2">
              Size:{' '}
              <span
                style={{
                  fontWeight: 'bold',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  padding: '8px',
                }}
              >
                {bottle.volume !== 0
                  ? `${bottle.volume} ${bottle.volumeUnit}`
                  : '-'}
              </span>
            </Typography>

            <Typography
              variant="body2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Color:
              {bottle?.color?.split('/').map((col: string, idx: number) => {
                const colorLabel = col.split('-')[0].trim();
                const hex = colorNameToHex(colorLabel);

                return (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: hex,
                      color: getContrastColor(hex),
                      padding: '4px 8px',
                      borderRadius: 20,
                      minWidth: 48,
                      display: 'inline-block',
                      textAlign: 'center',
                    }}
                  >
                    {col.trim()}
                  </span>
                );
              })}
            </Typography>
          </Box>

          {/* Unit Type */}
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              label="Unit Type"
              size="small"
              fullWidth
              value={unitType}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) =>
                onUnitTypeChange(bottle, event.target.value)
              }
            >
              {getUnitTypeOptions(bottle).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Multiplier & Units */}
          <Grid
            container
            spacing={1}
            sx={{ mt: 2, mb: 1.5 }}
            onClick={(event) => event.stopPropagation()}
          >
            {/* Multiplier */}
            <Grid item xs={12} sm={8} md={8} lg={8}>
              {isDisabled ? (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    pt: 1,
                  }}
                >
                  Quantity:{' '}
                  <span
                    style={{
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      padding: '8px',
                    }}
                  >
                    {bottle.bulkQuantity}
                  </span>
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label="Multiplier"
                    size="small"
                    type="number"
                    value={multiplier}
                    disabled={isDisabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);

                      if (!isNaN(val) && val >= 1 && val <= 6) {
                        onMultiplierChange(bottle.id, val);
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: 6,
                      style: {
                        textAlign: 'center',
                        padding: '6px 0',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            size="small"
                            disabled={isDisabled}
                            onClick={(event) => {
                              event.stopPropagation();
                              onMultiplierChange(
                                bottle.id,
                                Math.max(1, multiplier - 1)
                              );
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0,
                              borderRadius: '4px',
                              color: '#555',
                              transition: 'all 0.2s ease',

                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                              },

                              '&:active': {
                                backgroundColor: '#bbdefb',
                                transform: 'scale(0.9)',
                              },
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </InputAdornment>
                      ),

                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            disabled={isDisabled}
                            onClick={(event) => {
                              event.stopPropagation();
                              onMultiplierChange(
                                bottle.id,
                                Math.min(6, multiplier + 1)
                              );
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0,
                              borderRadius: '4px',
                              color: '#555',
                              transition: 'all 0.2s ease',

                              '&:hover': {
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                              },

                              '&:active': {
                                backgroundColor: '#c8e6c9',
                                transform: 'scale(0.9)',
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 150,

                      '& .MuiInputBase-root': {
                        height: 35,
                      },

                      '& .MuiInputLabel-root': {
                        fontSize: 12,
                      },

                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },

                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    }}
                  />
                </Box>
              )}
            </Grid>

            {/* Units */}
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                label="Units"
                type="number"
                size="small"
                value={quantity === undefined ? 0 : quantity}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                  const value = event.target.value;

                  onQuantityChange(
                    bottle.id,
                    value === '' ? '' : Number(value)
                  );
                }}
                onBlur={(event) =>
                  onQuantityBlur(bottle.id, event.target.value)
                }
                inputProps={{ min: 0 }}
                disabled={selected}
              />
            </Grid>
          </Grid>


        </CardContent>
        <CardActions sx={{ flexDirection: 'column', gap: 1.5, p: 2, pt: 0, mt: 1 }}>
          <Button
            variant="outlined"
            size="medium"
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              onViewDetails(bottle, quantity);
            }}
            sx={{ fontWeight: 600 }}
          >
            View Details
          </Button>
          <Tooltip
            title={showClientTooltip ? 'Please select client to proceed for order' : ''}
            arrow
          >
            <span style={{ width: '100%' }}>
              <Button
                variant="contained"
                size="medium"
                fullWidth
                onClick={(event) => {
                  event.stopPropagation();
                  onAddToCart(bottle);
                }}
                sx={{ fontWeight: 600 }}
                disabled={isClientSelectionRequired || Number(quantity || 0) <= 0}
              >
                Add to Cart
              </Button>
            </span>
          </Tooltip>
        </CardActions>
      </Card>
    </Grid>
  );
}, (prevProps, nextProps) => (
  prevProps.bottle === nextProps.bottle &&
  prevProps.selected === nextProps.selected &&
  prevProps.quantity === nextProps.quantity &&
  prevProps.unitType === nextProps.unitType &&
  prevProps.multiplier === nextProps.multiplier &&
  prevProps.imageHeight === nextProps.imageHeight &&
  prevProps.isClientSelectionRequired === nextProps.isClientSelectionRequired &&
  prevProps.showClientTooltip === nextProps.showClientTooltip
));

interface SupplementalItemCardProps {
  item: any;
  selected: boolean;
  quantity: number | "";
  imageHeight: number;
  isClientSelectionRequired: boolean;
  imageFit: 'contain' | 'cover';
  onToggleSelected: (item: any) => void;
  onOpenImagePreview: (imageSrc: string | undefined, title: string) => void;
  onViewDetails: (item: any, quantity: number | "") => void;
  onQuantityChange: (id: number, value: number | "") => void;
  onQuantityBlur: (id: number, value: string) => void;
  onAddToCart: (item: any) => void;
}

const SupplementalItemCard = memo(({
  item,
  selected,
  quantity,
  imageHeight,
  isClientSelectionRequired,
  imageFit,
  onToggleSelected,
  onOpenImagePreview,
  onViewDetails,
  onQuantityChange,
  onQuantityBlur,
  onAddToCart
}: SupplementalItemCardProps) => {
  const imageSrc = item?.id ? QUALITY_IMAGE_MAP[item.id] : undefined;
  const hasImage = !!imageSrc;

  return (
    <Grid item xs={12} sm={6} key={item.id}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          border: selected ? '2px solid #003883' : '1px solid #e0e0e0',
          borderRadius: 2,
          transition: 'border-color 0.15s',
          '&:hover': {
            borderColor: '#003883',
          }
        }}
      >
        <Box
          onClick={() => onToggleSelected(item)}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: imageHeight,
            flex: `0 0 ${imageHeight}px`,
            backgroundColor: '#f5f8fc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover .zoom-btn': { opacity: 1, pointerEvents: 'auto' }
          }}
        >
          {selected && (
            <Box
              onClick={(event) => { event.stopPropagation(); onToggleSelected(item); }}
              role="checkbox"
              aria-checked={true}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#003883',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ color: '#fff', fontSize: '12px', fontWeight: 700, lineHeight: 1 }}>✓</Box>
            </Box>
          )}
          {hasImage ? (
            <CardMedia
              component="img"
              height={imageHeight}
              loading="lazy"
              image={imageSrc}
              alt={item.name}
              sx={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: imageFit,
                objectPosition: 'center center',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
                gap: 1
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#003883',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: 1.3
                }}
              >
                {item.name}
              </Typography>
            </Box>
          )}
          {hasImage && (
            <IconButton
              className="zoom-btn"
              onClick={(event) => {
                event.stopPropagation();
                onOpenImagePreview(imageSrc, item.name);
              }}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255,255,255,0.85)',
                opacity: 0,
                pointerEvents: 'none'
              }}
              aria-label="Zoom image"
            >
              <ZoomInIcon />
            </IconButton>
          )}
        </Box>
        <CardContent sx={{ textAlign: 'left', p: 2, pb: 1, flex: '1 0 auto' }}>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#003883', minHeight: 38, mb: 1 }}>
            {item.name}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '0.9rem', color: '#2c3e50', minHeight: 38, mb: 1 }}>
            {item.comments !== '' ? item.comments : '-'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#5a6c7d', fontWeight: 500, backgroundColor: '#f0f4f8', padding: '4px 8px', borderRadius: 1, flex: 1, fontSize: '0.85rem' }}>
              {item.size === 0 || !item.size ? '-' : item.size}
            </Typography>
            <TextField
              label="Units"
              type="number"
              size="small"
              onClick={(event) => event.stopPropagation()}
              value={quantity === undefined ? 0 : quantity}
              onChange={(event) => {
                const value = event.target.value;
                onQuantityChange(item.id, value === '' ? '' : Number(value));
              }}
              onBlur={(event) => onQuantityBlur(item.id, event.target.value)}
              inputProps={{ min: 0, style: { width: 60 } }}
              disabled={selected}
            />
          </Box>
        </CardContent>
        <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 0.5, mt: 'auto' }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              onViewDetails(item, quantity);
            }}
            sx={{ fontWeight: 600, fontSize: '0.8rem' }}
          >
            View Details
          </Button>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              onAddToCart(item);
            }}
            sx={{ fontWeight: 600, fontSize: '0.8rem' }}
            disabled={isClientSelectionRequired || Number(quantity || 0) <= 0}
          >
            Add to Cart
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
}, (prevProps, nextProps) => (
  prevProps.item === nextProps.item &&
  prevProps.selected === nextProps.selected &&
  prevProps.quantity === nextProps.quantity &&
  prevProps.imageHeight === nextProps.imageHeight &&
  prevProps.isClientSelectionRequired === nextProps.isClientSelectionRequired &&
  prevProps.imageFit === nextProps.imageFit
));

export default function BottleOrderPage() {
  const [quantities, setQuantities] = useState<{ [id: number]: number | "" }>({});
  const [itemUnitTypes, setItemUnitTypes] = useState<Record<number, string>>({});
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [reviewSelectedOpen, setReviewSelectedOpen] = useState(false);
  const [sharedSelectedUnits, setSharedSelectedUnits] = useState<number | "">(1);
  const [selectedBottle, setSelectedBottle] = useState<any>(null);
  const [units, setUnits] = useState<number | "">(0);
  const [detailsImageZoomed, setDetailsImageZoomed] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null);
  const [previewImageTitle, setPreviewImageTitle] = useState('');
  const [previewImageZoomed, setPreviewImageZoomed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedBottleTab, setSelectedBottleTab] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    data: [],
    isLoading: false
  });
  const [clientList, setClientList] = useState<ClientOption[]>([]);
  const [isUnitTypeChanged, setIsUnitTypeChanged] = useState<Record<number, boolean>>({});
  const jwt: any = localStorage.getItem('token');
  const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;
  const [searchClientValue, setSearchClientValue] = useState('');
  const hasLoadedTabs = useRef<{ [key: number]: boolean }>({});
  const containerRequest = useRef<Promise<void> | null>(null);
  const { refreshCartItems, selectedClientId, eskiesToFit, setEskiesToFit, setSelectedClientId, isClientSelected, updateClientSelected } = useCart();
  const [selectedMatrix, setSelectedMatrix] = useState<any>([]);
  const [testQuery, setTestQuery] = useState<string>('');
  const [testOptions, setTestOptions] = useState<Array<{ id: string | number; name: string }>>([]);
  const [loadingTests, setLoadingTests] = useState<boolean>(false);
  const [selectedTest, setSelectedTest] = useState<{ id: string | number; name: string } | null>(null);
  const [makeGroup, setMakeGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [visibleContainerCount, setVisibleContainerCount] = useState(12);
  const [multipliers, setMultipliers] = useState<{ [id: number]: number }>({});
  const deferredSearch = useDeferredValue(search);

  // Demo modal state
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [dontShowDemoAgain, setDontShowDemoAgain] = useState(() => {
    const saved = localStorage.getItem('dontShowBottleOrderDemo');
    return saved ? JSON.parse(saved) : false;
  });

  const location = useLocation();

  const demoSteps = [
    {
      id: 1,
      title: 'Welcome to Bottle Order',
      description: 'Browse and select containers for your order. Click on any item to view detailed information and specifications.',
      mediaUrl: require('assets/container_images/BAG_4-05.jpg'),
      mediaType: 'image' as const,
    },
    {
      id: 2,
      title: 'Add Items to Cart',
      description: 'Select the quantity you need and click "Add to Cart" to include items in your order. You can select multiple items.',
      mediaUrl: undefined,
      mediaType: 'image' as const,
    },
    {
      id: 3,
      title: 'Review Your Selection',
      description: 'Review all selected items in the popup, adjust quantities if needed, and proceed to checkout when ready.',
      mediaUrl: undefined,
      mediaType: 'image' as const,
    },
    {
      id: 4,
      title: 'Complete Your Order',
      description: 'Click "Add Selected Items To Cart" to finalize your selection and proceed with the checkout process.',
      mediaUrl: undefined,
      mediaType: 'image' as const,
    }
  ];

  const clearState = () => {
    resetFilters();
    setTestQuery('');
    setTestOptions([]);
    setSearch('');
    setSelectedMatrix([]);
  };

  useEffect(() => {
    if (location.state?.activeTab === 1) {
      clearState();
      setActiveTab(1);
      qualityAndPackingApiCall();

      // Clear navigation state so effect won't run again
      window.history.replaceState({}, document.title);
    } else {
      clearState();
      setActiveTab(0);
      containerApiCall();
    }
  }, [location.state]);

  // Reset selectedClientId to -1 on page load
  useEffect(() => {
    if (labUserCheck?.UserType === 'Internal') {
      setSelectedClientId(-1);
    }
  }, []);

  useEffect(() => {
    if (labUserCheck?.UserType === 'Internal' && selectedClientId == -1) {
      setSearchClientValue('');
      clearSelectedItems();
      setTestQuery('');
      setTestOptions([]);
      setSearch('');
      setSelectedMatrix([]);
      resetFilters();
      reloadDefault();
    }
  }, [selectedClientId]);

  useEffect(() => {
    // Prevent double call per tab
    if (hasLoadedTabs.current[activeTab]) {
      return;
    }

    hasLoadedTabs.current[activeTab] = true;

    reloadDefault();
  }, [activeTab]);

  // Show demo modal on page load if user hasn't disabled it
  useEffect(() => {
    if (!dontShowDemoAgain) {
      setDemoModalOpen(true);
    }
  }, []);

  const handleDontShowDemoAgain = (checked: boolean) => {
    setDontShowDemoAgain(checked);
    localStorage.setItem('dontShowBottleOrderDemo', JSON.stringify(checked));
  };

  const handleCloseDemoModal = () => {
    setDemoModalOpen(false);
  };

  const qualityAndPackingApiCall = async () => {
    try {
      await getQualityAndPackingItems().then((res: any) => {
        if (res.status === 200) {
          setPaginationModel(old => ({ ...old, isLoading: false, data: res.data, total: res.data.length }))
        }
      }).catch((error) => {
        setPaginationModel(old => ({ ...old, isLoading: false }));
        hasLoadedTabs.current[activeTab] = false;
      });
    } catch (err) {
      setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
    }
  }

  const containerApiCall = () => {
    if (containerRequest.current) {
      return containerRequest.current;
    }

    containerRequest.current = (async () => {
      try {
        const res = await getAllContainers();
        if (res.status === 200) {
          setPaginationModel(old => ({ ...old, isLoading: false, data: res.data, total: res.data.length }));
        } else {
          setPaginationModel(old => ({ ...old, isLoading: false }));
          hasLoadedTabs.current[0] = false;
        }
      } catch (err) {
        setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
        hasLoadedTabs.current[0] = false;
      }
    })().finally(() => {
      containerRequest.current = null;
    });

    return containerRequest.current;
  }

  const reloadDefault = async () => {
    setPaginationModel(old => ({ ...old, isLoading: true }));
    try {
      if (activeTab === 0) {
        await containerApiCall();
      } else {
        await qualityAndPackingApiCall();
      }
    } catch (err) {
      setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
    }
  };

  const clientSearchDebounce = useRef<number | null>(null);

  useEffect(() => {
    if (selectedClientId && selectedClientId !== -1) {
      return;
    }
    if (clientSearchDebounce.current) {
      window.clearTimeout(clientSearchDebounce.current);
    }

    if (searchClientValue.trim().length === 0) {
      setClientList([]);
      return;
    }

    clientSearchDebounce.current = window.setTimeout(() => {
      handleClientNameSearch(searchClientValue);
    }, 350);

    return () => {
      if (clientSearchDebounce.current) {
        window.clearTimeout(clientSearchDebounce.current);
      }
    };
  }, [searchClientValue]);

  const searchDebounce = useRef<number | null>(null);
  const isSelectionRef = useRef(false);

  const fetchTests = async (q: string) => {
    if (!q || q.length < 2) {
      setTestOptions([]);
      return;
    }
    setLoadingTests(true);
    try {
      const res = await searchTestResult(q); // adjust endpoint
      if (res.status !== 200) throw new Error('Failed to fetch tests');
      const data = res.data;
      setTestOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      setTestOptions([]);
    } finally {
      setLoadingTests(false);
    }
  };

  useEffect(() => {
    if (isSelectionRef.current) {
      isSelectionRef.current = false;
      return;
    }
    // debounce input
    if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
    searchDebounce.current = window.setTimeout(() => {
      fetchTests(testQuery);
    }, 350);
    return () => {
      if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
    };
  }, [testQuery]);

  const loadBottleOrderForTest = async (testId: string | number | null) => {
    if (!testId) {
      // Reload default list when test filter is cleared
      reloadDefault();
      return;
    }
    setPaginationModel(old => ({ ...old, isLoading: true }));
    try {
      const res = await filterContainerBasedOnTest(testId);
      if (res.status === 200) {
        setPaginationModel(old => ({ ...old, isLoading: false, data: res.data, total: res.data.length }));
      } else {
        setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
      }
    } catch (err) {
      setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
    }
  };

  const handleClientNameSearch = (value: string) => {
    standardQuoteSearch(value).then((res: any) => {
      if (res.status === 200) {
        setClientList(res.data);
      }
    }).catch((error) => {
      setClientList([]);
    });
  }

  const handleViewDetails = useCallback((bottle: any, quantityValue: number | "" = 0) => {
    setSelectedBottle({ ...bottle, isContainer: true });
    setUnits(quantityValue === undefined ? 0 : quantityValue);
    setSelectedBottleTab(activeTab);
    setDetailsOpen(true);
    setDetailsImageZoomed(false);
  }, [activeTab]);

  const handleDialogUnitsChange = (value: number | "") => {
    setUnits(value);

    if (selectedBottle) {
      setQuantities((prev) => ({
        ...prev,
        [selectedBottle.id]: value === "" ? "" : value,
      }));
    }
  };

  const handleQuantityChange = useCallback((id: number, value: number | "") => {
    setQuantities((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleQuantityBlur = useCallback((id: number, value: string) => {
    const parsedValue = Number(value);

    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      handleQuantityChange(id, 0);
    }
  }, [handleQuantityChange]);

  const getItemUnitType = (item: any) => itemUnitTypes[item.id] ?? 'Individual';

  const getMultiplierValue = (id: number) =>
    multipliers[id] ?? paginationModel.data.find((item: any) => item.id === id)?.multiplier ?? 1;

  const handleMultiplierChange = useCallback((id: number, value: number) => {
    setMultipliers((prev) => ({ ...prev, [id]: Math.min(6, Math.max(1, value)) }));
  }, []);

  const handleItemUnitTypeChange = useCallback((item: any, value: string) => {
    setItemUnitTypes((prev) => ({
      ...prev,
      [item.id]: value
    }));

    setIsUnitTypeChanged((prev) => ({
      ...prev,
      [item.id]: value !== "Individual"
    }));
  }, []);

  const clearSelectedItems = () => {
    setQuantities((prev) => {
      const next = { ...prev };

      selectedItemIds.forEach((selectedId) => {
        next[selectedId] = 0;
      });

      return next;
    });
    setItemUnitTypes((prev) => {
      const next = { ...prev };

      selectedItemIds.forEach((selectedId) => {
        delete next[selectedId];
      });

      return next;
    });
    setMultipliers((prev) => {
      const next = { ...prev };
      selectedItemIds.forEach((selectedId) => { delete next[selectedId]; });
      return next;
    });
    setSelectedItemIds([]);
    setSharedSelectedUnits(0);
    setReviewSelectedOpen(false);
    setMakeGroup(false);
    setGroupName('');
  };

  // Initialize cart items when component mounts
  useEffect(() => {
    if (selectedClientId && selectedClientId !== -1 && labUserCheck?.UserType !== 'Internal') {
      refreshCartItems(selectedClientId);
    } else if ((!selectedClientId || selectedClientId === -1) && labUserCheck?.UserType === 'Internal') {
      setSelectedClientId(-1);
    }
  }, [selectedClientId]);

  useEffect(() => {
    return () => {
      if (labUserCheck?.UserType === 'Internal') {
        setSelectedClientId(-1);
      }
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    hasLoadedTabs.current[newValue] = false;
    resetFilters();
    setSearch('');
    setTestQuery('');
    setTestOptions([]);
    setSelectedMatrix([]);
  };

  const resetFilters = () => {
    setPaginationModel({
      page: 1,
      pageSize: 10,
      total: 0,
      data: [],
      isLoading: false
    });
    setQuantities({});
    setItemUnitTypes({});
    setSelectedItemIds([]);
    setSharedSelectedUnits(0);
    setReviewSelectedOpen(false);
    setSelectedTest(null);
    setMakeGroup(false);
    setGroupName('');
    setMultipliers({});
  }

  const getQuantityValue = (id: number) => {
    const value = quantities[id];

    if (value === "" || value === undefined || Number.isNaN(value)) {
      return 0;
    }

    return Number(value);
  };

  const getItemsToAdd = () => {
    return paginationModel.data.filter((item: any) => getQuantityValue(item.id) > 0);
  };

  const getSelectedItems = () => {
    return paginationModel.data.filter((item: any) => selectedItemIds.includes(item.id));
  };

  const toggleSelectedItem = useCallback((item: any) => {
    const isSelected = selectedItemIds.includes(item.id);

    if (isSelected) {
      setSelectedItemIds((prev) => prev.filter((selectedId) => selectedId !== item.id));
      setItemUnitTypes((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setQuantities((prev) => ({
        ...prev,
        [item.id]: 0
      }));
      setSharedSelectedUnits(0);
      return;
    }

    setSelectedItemIds((prev) => [...prev, item.id]);
    setItemUnitTypes((prev) => ({
      ...prev,
      [item.id]: prev[item.id] || 'Individual'
    }));
    setSharedSelectedUnits(0);
    setQuantities((prev) => ({
      ...prev,
      [item.id]: 0
    }));
  }, [selectedItemIds]);

  const handleSharedSelectedUnitsChange = (value: number | "") => {
    setSharedSelectedUnits(value);

    setQuantities((prev) => {
      const next = { ...prev };

      selectedItemIds.forEach((selectedId) => {
        next[selectedId] = value === "" ? "" : value;
      });

      return next;
    });
  };

  const handleSharedSelectedUnitsBlur = (value: string) => {
    const parsedValue = Number(value);

    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      handleSharedSelectedUnitsChange(0);
    }
  };

  const handleCloseReviewPopup = () => {
    setSharedSelectedUnits(0);
    setQuantities((prev) => {
      const next = { ...prev };
      selectedItemIds.forEach((id) => { next[id] = 0; });
      return next;
    });
    setReviewSelectedOpen(false);
  };

  const handleOpenSelectedReview = () => {
    const selectedItems = getSelectedItems();

    if (!selectedItems.length) {
      return;
    }

    setSharedSelectedUnits(0);
    setMakeGroup(false);
    setGroupName(selectedTest?.name || '');
    setReviewSelectedOpen(true);
  };

  const handleAddSelectedItemsToCart = async () => {
    const selectedItems = getSelectedItems();
    const resolvedGroupName = makeGroup ? groupName.trim() : '';

    if (!selectedItems.length) {
      return;
    }

    try {
      const response = await addDraftCheckOutItem([
        buildDraftCheckoutPayload(
          selectedItems.map((item: any) => buildDraftCheckoutItem(item, getQuantityValue(item.id), {
            bulkType: getItemUnitType(item),
            multiplier: getMultiplierValue(item.id)
          })),
          resolvedGroupName
            ? {
              groupName: resolvedGroupName,
              size: sharedSelectedUnits === '' ? 0 : Number(sharedSelectedUnits)
            }
            : undefined
        )]
      );

      if (response.status !== 200) {
        return;
      }

      setQuantities((prev) => {
        const next = { ...prev };

        selectedItems.forEach((item: any) => {
          next[item.id] = 0;
        });

        return next;
      });
      setItemUnitTypes((prev) => {
        const next = { ...prev };

        selectedItems.forEach((item: any) => {
          delete next[item.id];
        });

        return next;
      });

      setMultipliers((prev) => {
        const next = { ...prev };
        selectedItems.forEach((item: any) => { delete next[item.id]; });
        return next;
      });

      setIsUnitTypeChanged((prev) => {
        const next = { ...prev };
        selectedItems.forEach((item: any) => {
          delete next[item.id];
        });
        return next;
      });

      setSelectedItemIds([]);
      setSharedSelectedUnits(0);
      setReviewSelectedOpen(false);
      setMakeGroup(false);
      setGroupName('');

      await refreshCartItems(selectedClientId);
    } catch (error) {
      // console.error('Error adding selected items to cart:', error);
    }
  };

  const buildDraftCheckoutItem = (item: any, noOfUnits: number, options?: { bulkType?: string; bulkQuantity?: number; isBulkOrder?: boolean; groupName?: string; multiplier?: number }) => {
    const comments = item?.comments ? ` - ${item.comments}` : '';
    const size = item?.size ? `${item.size}` : '';
    const resolvedGroupName = options?.groupName?.trim() || '';
    const resolvedBulkType = activeTab === 0 ? (options?.bulkType || getItemUnitType(item)) : null;
    const isBulkOrder = isUnitTypeChanged[item.id] ?? false;

    return {
      clientId: selectedClientId,
      [activeTab === 0 ? 'containerID' : 'PackageID']: item.id,
      containerName: item.name,
      containerDesc: activeTab === 0 ? item.description : size + comments,
      noOfUnits,
      multiplier: options?.multiplier ?? 1,
      ...(resolvedBulkType ? { bulkType: resolvedBulkType } : {}),
      isBulkOrder,
      bulkQuantity: isBulkOrder ? item?.bulkQuantity : 0,
      groupName: resolvedGroupName,
      isPackage: activeTab === 1,
      eskiesToFit
    };
  };

  const buildDraftCheckoutPayload = (items: any[], options?: { groupName?: string; size?: number }) => {
    const resolvedGroupName = options?.groupName?.trim() || '';

    if (resolvedGroupName) {
      return {
        draftBottleGroup: {
          name: resolvedGroupName,
          size: options?.size ?? 0,
          clientID: selectedClientId,
          draftItems: items
        }
      };
    }

    return {
      draftItems: items,
    };
  };

  const handleAddToCart = async (bottle: any) => {
    const currentUnits = getQuantityValue(bottle.id);

    if (currentUnits <= 0) {
      return;
    }

    const createObj = buildDraftCheckoutPayload([
      buildDraftCheckoutItem(bottle, currentUnits, { multiplier: getMultiplierValue(bottle.id) })
    ]);
    setDetailsOpen(false);

    try {
      const response = await addDraftCheckOutItem([createObj]);
      if (response.status === 200) {
        setSelectedItemIds((prev) => prev.filter((selectedId) => selectedId !== bottle.id));
        setQuantities((prev) => ({
          ...prev,
          [bottle.id]: 0
        }));
        setMultipliers((prev) => {
          const next = { ...prev };
          delete next[bottle.id];
          return next;
        });
        setUnits(0);
        setIsUnitTypeChanged((prev) => {
          const next = { ...prev };
          delete next[bottle.id];
          return next;
        });
        await refreshCartItems(selectedClientId);
      }
    } catch (error) {
      // console.error('Error adding to cart:', error);      
    }
  };

  const handleAddModifiedItemsToCart = async () => {
    const itemsToAdd = getItemsToAdd();
    if (!itemsToAdd.length) {
      return;
    }

    try {
      const response = await addDraftCheckOutItem([
        buildDraftCheckoutPayload(
          itemsToAdd.map((item: any) => buildDraftCheckoutItem(item, getQuantityValue(item.id), {
            bulkType: getItemUnitType(item),
            multiplier: getMultiplierValue(item.id)
          }))
        )
      ]);

      if (response.status !== 200) {
        return;
      }

      modifiedItemsReset(itemsToAdd);

      await refreshCartItems(selectedClientId);
    } catch (error) {
      // console.error('Error adding modified items to cart:', error);
    }
  };

  const modifiedItemsReset = (itemsToAdd: any[]) => {
    setQuantities((prev) => {
      const next = { ...prev };

      itemsToAdd.forEach((item: any) => {
        next[item.id] = 0;
      });

      return next;
    });

    setMultipliers((prev) => {
      const next = { ...prev };
      itemsToAdd.forEach((item: any) => { delete next[item.id]; });
      return next;
    });

    setIsUnitTypeChanged((prev) => {
      const next = { ...prev };
      itemsToAdd.forEach((item: any) => {
        delete next[item.id];
      });
      return next;
    });
  }

  const getBottleImageUrl = (bottle: any) => {
    const mapped = bottle?.id ? BOTTLE_IMAGE_MAP[bottle.id] : undefined;
    return mapped;
  };

  const hasBottleImage = (bottle: any) => {
    return !!(bottle?.id && BOTTLE_IMAGE_MAP[bottle.id]);
  };

  const getQualityPackingImageUrl = (item: any) => {
    const mapped = item?.id ? QUALITY_IMAGE_MAP[item.id] : undefined;
    return mapped;
  };

  const hasQualityPackingImage = (item: any) => {
    return !!(item?.id && QUALITY_IMAGE_MAP[item.id]);
  };

  const openImagePreview = useCallback((imageSrc: string | undefined, title: string) => {
    if (!imageSrc) {
      return;
    }

    setPreviewImageSrc(imageSrc);
    setPreviewImageTitle(title);
    setPreviewImageZoomed(false);
    setImagePreviewOpen(true);
  }, []);

  const closeImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImageSrc(null);
    setPreviewImageTitle('');
    setPreviewImageZoomed(false);
  };
  const matrixCounts: Record<number, number> = useMemo(() => MATRIX_BUTTONS.reduce((acc, btn) => {
    acc[btn.id] = paginationModel.data.filter((b: any) => b.matrixTypeId === btn.id).length;
    return acc;
  }, {} as Record<number, number>), [paginationModel.data]);

  const filteredData = useMemo(() => {
    const searchLower = deferredSearch.trim().toLowerCase();

    return paginationModel.data.filter((bottle: any) => {
      const searchMatch =
        !searchLower ||
        bottle.name?.toLowerCase().includes(searchLower) ||
        bottle.color?.toLowerCase().includes(searchLower) ||
        bottle.code?.toLowerCase().includes(searchLower) ||
        (bottle.volume && bottle.volume.toString().includes(searchLower)) ||
        (bottle.volumeUnit && bottle.volumeUnit.toLowerCase().includes(searchLower));

      const matrixMatch =
        activeTab !== 0 ||
        selectedMatrix.length === 0 ||
        selectedMatrix.includes(bottle.matrixTypeId);

      return searchMatch && matrixMatch;
    });
  }, [activeTab, deferredSearch, paginationModel.data, selectedMatrix]);

  // Reset visible count whenever the filtered list changes (new data, search, matrix filter)
  useEffect(() => {
    setVisibleContainerCount(12);
  }, [filteredData]);

  const filteredQualityItems = useMemo(() => filteredData.filter((item: any) => item.group === 'Quality'), [filteredData]);
  const filteredPackingItems = useMemo(() => filteredData.filter((item: any) => item.group === 'Packing'), [filteredData]);
  const selectedItemIdSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);
  const selectedItems = useMemo(() => paginationModel.data.filter((item: any) => selectedItemIdSet.has(item.id)), [paginationModel.data, selectedItemIdSet]);
  const modifiedItems = useMemo(() => paginationModel.data.filter((item: any) => getQuantityValue(item.id) > 0), [paginationModel.data, quantities]);
  const hasSelectedItemsWithZeroQuantity = selectedItems.some((item: any) => getQuantityValue(item.id) <= 0);
  const canShowGroupOption = activeTab === 0 && selectedItems.length > 1;
  const hasSelectedBulkUnitType = selectedItems.some((item: any) => getItemUnitType(item) !== 'Individual');
  const isGroupingNameInvalid = makeGroup && !groupName.trim();
  const isClientSelectionRequired =
    labUserCheck?.UserType === 'Internal' && !isClientSelected;

  let intenalUserCheck = (labUserCheck?.Permission?.includes('StandardQuotes') || labUserCheck?.Permission === 'StandardQuotes') ? '/home/viewQuote' : '/home/admin';

  let pathChange = (labUserCheck && (labUserCheck?.UserType === 'Internal')) ? intenalUserCheck : '/home';

  let nav: any = [{ name: 'Home', path: pathChange }, { name: 'Order History', path: '/home/orderHistory' }, { name: 'Catalogue', path: '/home/bottleOrderList' }];
  const containerCardImageHeight = 240;
  const qualityPackingCardImageHeight = 180;

  const renderSelectionCheckbox = (item: any) => (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        toggleSelectedItem(item);
      }}
      role="checkbox"
      aria-checked={selectedItemIds.includes(item.id)}
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 42,
        height: 42,
        borderRadius: '50%',
        background: selectedItemIds.includes(item.id)
          ? 'linear-gradient(135deg, rgba(0, 56, 131, 0.98) 0%, rgba(6, 88, 187, 0.95) 100%)'
          : 'rgba(255,255,255,0.94)',
        border: selectedItemIds.includes(item.id)
          ? '1px solid rgba(0, 56, 131, 0.92)'
          : '1px solid rgba(255,255,255,0.72)',
        boxShadow: selectedItemIds.includes(item.id)
          ? '0 12px 26px rgba(0, 56, 131, 0.26)'
          : '0 10px 20px rgba(15, 23, 42, 0.16)',
        backdropFilter: 'blur(10px)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: selectedItemIds.includes(item.id)
            ? '0 14px 30px rgba(0, 56, 131, 0.3)'
            : '0 12px 24px rgba(15, 23, 42, 0.2)'
        }
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: selectedItemIds.includes(item.id) ? '#fff' : 'transparent',
          border: selectedItemIds.includes(item.id) ? 'none' : '2px solid #003883',
          color: '#003883',
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: 1
        }}
      >
        {selectedItemIds.includes(item.id) ? '✓' : ''}
      </Box>
    </Box>
  );

  const renderUnitTypeField = (item: any) => (
    <TextField
      select
      label="Unit Type"
      size="small"
      fullWidth
      value={getItemUnitType(item)}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => handleItemUnitTypeChange(item, event.target.value)}
    >
      {getUnitTypeOptions(item).map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );

  const clients = JSON.parse(
    labUserCheck?.ClientPermissions || '[]'
  );

  const clientName = clients.find(
    x => String(x?.ClientID) === String(selectedClientId)
  )?.ClientName;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
        <Breadcrumb navList={nav} />
      </div>
      {labUserCheck?.UserType === 'External' ? (
        <Typography variant="h6" gutterBottom align="left" mb={2}>
          {`${clientName}`}
        </Typography>
      ) : null}
      <Typography variant="h6" gutterBottom align="left" mb={2}>
        Catalogue
      </Typography>
      {labUserCheck?.UserType === 'Internal' && <Grid container spacing={2} style={{ marginLeft: '0px', paddingBottom: '8px' }} alignItems="flex-start">
        <Grid item xs={12} md={3} style={{ paddingLeft: 0 }}>
          <Autocomplete<ClientOption, false, false, true>
            freeSolo
            options={clientList}
            inputValue={searchClientValue}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setSearchClientValue(value);
                setSelectedClientId(-1);
              }
            }}
            loading={paginationModel.isLoading}
            onChange={(event, value: ClientOption | string | null) => {
              if (value && typeof value !== 'string') {
                setSelectedClientId(value.id, value.name);
                setSearchClientValue(formatClientOption(value));
                updateClientSelected(true);
              } else {
                setSelectedClientId(-1);
                setSearchClientValue('');
                updateClientSelected(false);
              }
            }}
            getOptionLabel={(option) => formatClientOption(option)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {formatClientOption(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Client (Name/Code/BU) to place an order"
                size="small"
                fullWidth
                error={selectedClientId === 0 || selectedClientId === -1}
                sx={{ backgroundColor: '#fff' }}
                helperText={
                  (selectedClientId === 0 || selectedClientId === -1) &&
                    searchClientValue.trim() === ''
                    ? 'Please select client to proceed for order'
                    : ''
                }
                FormHelperTextProps={{ sx: { color: '#d32d2f' } }}
              />
            )}
            noOptionsText="No clients found"
            sx={{ backgroundColor: '#fff' }}
          />
        </Grid>
        <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '16px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Demo Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowDemoAgain}
                  onChange={(e) => {
                    handleDontShowDemoAgain(e.target.checked);
                    if (!e.target.checked) {
                      setDemoModalOpen(true);
                    }
                  }}
                  sx={{ color: '#003883', '&.Mui-checked': { color: '#003883' } }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: '#2c3e50',
                    cursor: 'pointer',
                    '&:hover': { color: '#003883' }
                  }}
                  onClick={() => setDemoModalOpen(true)}
                >
                  Demo
                </Typography>
              }
              sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: '#2c3e50' } }}
            />
            {activeTab === 1 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={eskiesToFit}
                    onChange={(e) => setEskiesToFit(e.target.checked)}
                    sx={{ color: '#003883', '&.Mui-checked': { color: '#003883' } }}
                  />
                }
                label="Eskies to fit"
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: '#2c3e50' } }}
              />
            )}
            {(selectedItems.length > 0 || modifiedItems.length > 0) && (
              <>
                <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                  {(selectedItems.length > 0 ? selectedItems.length : modifiedItems.length)} item{(selectedItems.length > 0 ? selectedItems.length : modifiedItems.length) > 1 ? 's' : ''} ready to add
                </Typography>
                {selectedItems.length > 0 && (
                  <Button variant="outlined" color="inherit" onClick={clearSelectedItems} sx={{ fontWeight: 600 }}>Clear All</Button>
                )}
                <Tooltip title={isClientSelectionRequired ? 'Please select client to proceed for order' : ''} arrow>
                  <span>
                    <Button variant="contained" onClick={handleOpenSelectedReview} disabled={isClientSelectionRequired || selectedItems.length === 0} sx={{ fontWeight: 600 }}>Review Selected Items</Button>
                  </span>
                </Tooltip>
                {selectedItems.length === 0 && (
                  <Tooltip title={isClientSelectionRequired ? 'Please select client to proceed for order' : ''} arrow>
                    <span>
                      <Button variant="contained" onClick={handleAddModifiedItemsToCart} disabled={isClientSelectionRequired} sx={{ fontWeight: 600 }}>Add Modified Items to Cart</Button>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
            <a
              href="https://www.eurofins.com.au/environment-testing/downloads/#:~:text=Valid%20until%2011.06.2028-,Sample%20Preservation%20and%20Sampling%20Guide,-Sample%20Preservation%20and"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '13px', fontWeight: 600, color: '#003883', textDecoration: 'underline', whiteSpace: 'nowrap' }}
            >
              Preservation Guidelines
            </a>
          </Box>
        </Grid>
      </Grid>}
      {labUserCheck?.UserType !== 'Internal' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, mt: 1, gap: 1.5, flexWrap: 'wrap', paddingRight: '16px' }}>
          {/* Demo Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowDemoAgain}
                onChange={(e) => {
                  handleDontShowDemoAgain(e.target.checked);
                  if (!e.target.checked) {
                    setDemoModalOpen(true);
                  }
                }}
                sx={{ color: '#003883', '&.Mui-checked': { color: '#003883' } }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#2c3e50',
                  cursor: 'pointer',
                  '&:hover': { color: '#003883' }
                }}
                onClick={() => setDemoModalOpen(true)}
              >
                Demo
              </Typography>
            }
            sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: '#2c3e50' } }}
          />
          {activeTab === 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={eskiesToFit}
                  onChange={(e) => setEskiesToFit(e.target.checked)}
                  sx={{ color: '#003883', '&.Mui-checked': { color: '#003883' } }}
                />
              }
              label="Eskies to fit"
              sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: '#2c3e50' } }}
            />
          )}
          {(selectedItems.length > 0 || modifiedItems.length > 0) && (
            <>
              <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                {(selectedItems.length > 0 ? selectedItems.length : modifiedItems.length)} item{(selectedItems.length > 0 ? selectedItems.length : modifiedItems.length) > 1 ? 's' : ''} ready to add
              </Typography>
              {selectedItems.length > 0 && (
                <Button variant="outlined" color="inherit" onClick={clearSelectedItems} sx={{ fontWeight: 600 }}>Clear All</Button>
              )}
              <Tooltip title={isClientSelectionRequired ? 'Please select client to proceed for order' : ''} arrow>
                <span>
                  <Button variant="contained" onClick={handleOpenSelectedReview} disabled={isClientSelectionRequired || selectedItems.length === 0} sx={{ fontWeight: 600 }}>Review Selected Items</Button>
                </span>
              </Tooltip>
              {selectedItems.length === 0 && (
                <Tooltip title={isClientSelectionRequired ? 'Please select client to proceed for order' : ''} arrow>
                  <span>
                    <Button variant="contained" onClick={handleAddModifiedItemsToCart} disabled={isClientSelectionRequired} sx={{ fontWeight: 600 }}>Add Modified Items to Cart</Button>
                  </span>
                </Tooltip>
              )}
            </>
          )}
          <a
            href="https://www.eurofins.com.au/environment-testing/downloads/#:~:text=Valid%20until%2011.06.2028-,Sample%20Preservation%20and%20Sampling%20Guide,-Sample%20Preservation%20and"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '13px', fontWeight: 600, color: '#003883', textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            Preservation Guidelines
          </a>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, height: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" mb={2}>Filter By</Typography>
            {activeTab === 0 && (
              <>
                <Typography variant="body1" mb={2} sx={{ fontWeight: 600, color: '#003883' }}>Matrix</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                  <FormGroup>
                    {MATRIX_BUTTONS.map((btn) => {
                      const isChecked = selectedMatrix.includes(btn.id);

                      return (
                        <FormControlLabel
                          key={btn.id}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // ADD
                                  setSelectedMatrix((prev) => [...prev, btn.id]);
                                } else {
                                  // REMOVE
                                  setSelectedMatrix((prev) =>
                                    prev.filter((id) => id !== btn.id)
                                  );
                                }
                              }}
                              sx={{
                                color: '#003883',
                                '&.Mui-checked': { color: '#003883' }
                              }}
                            />
                          }
                          label={`${btn.label} (${matrixCounts[btn.id] || 0})`}
                          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </>
            )}
            <Typography variant="body1" mb={2} sx={{ fontWeight: 600, color: '#003883' }}>Category</Typography>
            <TextField
              placeholder="Search by name, code, color, or size"
              size="small"
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {activeTab === 0 && (<>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" mb={2} sx={{ fontWeight: 600, color: '#003883' }}>Test</Typography>
              <Autocomplete
                fullWidth
                options={testOptions}
                getOptionLabel={(option) => (option as any).name || ''}
                loading={loadingTests}
                value={selectedTest}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    setTestQuery(value);
                  }
                  if (reason === 'clear' || (reason === 'input' && value === '')) {
                    setSelectedTest(null);
                    setTestOptions([]);
                    reloadDefault();
                  }
                }}
                inputValue={testQuery}
                onChange={(_, value) => {
                  setSelectedTest(value as any);
                  if (value) {
                    isSelectionRef.current = true;
                    setTestQuery((value as any).name || '');
                    loadBottleOrderForTest((value as any).id);
                  } else {
                    setTestQuery('');
                    setTestOptions([]);
                    reloadDefault();
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter By Test"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTests ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: '40px',
              padding: 0,
              '& .MuiTabs-indicator': {
                height: '100%',
                borderRadius: '40px',
                backgroundColor: '#003883',
                zIndex: 0
              },
              '& .MuiTabs-flexContainer': {
                gap: 1
              }
            }}
          >
            <Tab
              icon={<InventoryIcon />}
              iconPosition="start"
              label="Container"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                minHeight: 48,
                borderRadius: '40px',
                px: 3,
                color: '#5a6c7d',
                zIndex: 1,
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  color: '#fff'
                },
                '&:hover': {
                  backgroundColor: activeTab !== 0 ? 'rgba(0,56,131,0.08)' : 'transparent'
                }
              }}
            />
            <Tab
              icon={<FactCheckOutlinedIcon />}
              iconPosition="start"
              label="Quality & Packing"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                minHeight: 48,
                borderRadius: '40px',
                px: 3,
                color: '#5a6c7d',
                zIndex: 1,
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  color: '#fff'
                },
                '&:hover': {
                  backgroundColor: activeTab !== 1 ? 'rgba(0,56,131,0.08)' : 'transparent'
                }
              }}
            />
          </Tabs>

          {activeTab === 0 && (
            <Grid container spacing={3} rowSpacing={4}>
              {paginationModel.isLoading ? (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Grid>
              ) : (
                filteredData.length > 0 ? (
                  <>
                    {filteredData.slice(0, visibleContainerCount).map((bottle) => (
                      <ContainerItemCard
                        key={bottle.id}
                        bottle={bottle}
                        selected={selectedItemIdSet.has(bottle.id)}
                        quantity={quantities[bottle.id] ?? 0}
                        unitType={getItemUnitType(bottle)}
                        multiplier={getMultiplierValue(bottle.id)}
                        imageHeight={containerCardImageHeight}
                        isClientSelectionRequired={isClientSelectionRequired}
                        showClientTooltip={labUserCheck?.UserType === 'Internal' && (selectedClientId === 0 || selectedClientId === -1)}
                        onToggleSelected={toggleSelectedItem}
                        onOpenImagePreview={openImagePreview}
                        onViewDetails={handleViewDetails}
                        onQuantityChange={handleQuantityChange}
                        onQuantityBlur={handleQuantityBlur}
                        onUnitTypeChange={handleItemUnitTypeChange}
                        onMultiplierChange={handleMultiplierChange}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                    {visibleContainerCount < filteredData.length && (
                      <Grid item xs={12} sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setVisibleContainerCount(prev => prev + 12)}
                        >
                          Load More ({filteredData.length - visibleContainerCount} remaining)
                        </Button>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography>No bottles found.</Typography>
                  </Grid>
                )
              )}
            </Grid>
          )}

          {activeTab === 1 && (
            <Box mt={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      color: '#003883',
                      borderBottom: '2px solid #003883',
                      paddingBottom: 1
                    }}
                  >
                    Quality
                  </Typography>
                  <Grid container spacing={2} rowSpacing={3}>
                    {filteredQualityItems.map((item) => (
                      <SupplementalItemCard
                        key={item.id}
                        item={item}
                        selected={selectedItemIdSet.has(item.id)}
                        quantity={quantities[item.id] ?? 0}
                        imageHeight={qualityPackingCardImageHeight}
                        isClientSelectionRequired={isClientSelectionRequired}
                        imageFit="contain"
                        onToggleSelected={toggleSelectedItem}
                        onOpenImagePreview={openImagePreview}
                        onViewDetails={handleViewDetails}
                        onQuantityChange={handleQuantityChange}
                        onQuantityBlur={handleQuantityBlur}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      color: '#003883',
                      borderBottom: '2px solid #003883',
                      paddingBottom: 1
                    }}
                  >
                    Packing
                  </Typography>
                  <Grid container spacing={2} rowSpacing={3}>
                    {filteredPackingItems.map((item) => (
                      <SupplementalItemCard
                        key={item.id}
                        item={item}
                        selected={selectedItemIdSet.has(item.id)}
                        quantity={quantities[item.id] ?? 0}
                        imageHeight={qualityPackingCardImageHeight}
                        isClientSelectionRequired={isClientSelectionRequired}
                        imageFit="cover"
                        onToggleSelected={toggleSelectedItem}
                        onOpenImagePreview={openImagePreview}
                        onViewDetails={handleViewDetails}
                        onQuantityChange={handleQuantityChange}
                        onQuantityBlur={handleQuantityBlur}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={imagePreviewOpen}
        onClose={closeImagePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            color: '#003883',
            fontWeight: 600
          }}
        >
          {previewImageTitle || 'Image Preview'}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              aria-label="close image preview"
              onClick={closeImagePreview}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                padding: 0,
                color: '#5a6c7d',
                fontSize: 24,
                lineHeight: 1,
                borderRadius: '50%'
              }}
            >
              ×
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#0f172a' }}>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              minHeight: { xs: 320, md: 560 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'radial-gradient(circle at center, rgba(30,41,59,0.92) 0%, rgba(15,23,42,1) 100%)',
              cursor: previewImageZoomed ? 'zoom-out' : 'zoom-in'
            }}
          >
            {previewImageSrc && (
              <Box
                component="img"
                src={previewImageSrc}
                alt={previewImageTitle}
                onClick={() => setPreviewImageZoomed((prev) => !prev)}
                sx={{
                  maxWidth: '100%',
                  maxHeight: { xs: '70vh', md: '78vh' },
                  objectFit: 'contain',
                  userSelect: 'none',
                  transformOrigin: 'center center',
                  transform: `scale(${previewImageZoomed ? 2.2 : 1})`,
                  transition: 'transform 0.2s ease-in-out',
                  cursor: previewImageZoomed ? 'zoom-out' : 'zoom-in'
                }}
              />
            )}
            <Box
              sx={{
                position: 'absolute',
                left: 16,
                bottom: 16,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                backgroundColor: 'rgba(15,23,42,0.72)',
                color: '#fff'
              }}
            >
              <Typography variant="body2">
                Click the image to zoom in or out.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reviewSelectedOpen}
        onClose={handleCloseReviewPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            pb: 2,
            borderBottom: '1px solid #e0e0e0',
            fontWeight: 600,
            fontSize: '18px',
            color: '#003883'
          }}
        >
          Review Selected Items
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {canShowGroupOption && (
            <Box
              sx={{
                mb: 2,
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: '#f8fbff',
                border: '1px solid #d9e6f7'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={makeGroup}
                    disabled={hasSelectedBulkUnitType}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setMakeGroup(checked);

                      if (checked && !groupName.trim()) {
                        setGroupName(selectedTest?.name || '');
                      }

                      if (!checked) {
                        setGroupName(selectedTest?.name || '');
                      }
                    }}
                    sx={{
                      color: '#003883',
                      '&.Mui-checked': { color: '#003883' }
                    }}
                  />
                }
                label="Form a Group?"
                sx={{ mb: makeGroup ? 1.5 : 0 }}
              />
              {makeGroup && (
                <TextField
                  label="Grouping Name"
                  fullWidth
                  size="small"
                  value={groupName}
                  onChange={(event) => setGroupName(event.target.value)}
                  required
                  error={isGroupingNameInvalid}
                  helperText={isGroupingNameInvalid ? 'Grouping Name is required' : ''}
                />
              )}
              {hasSelectedBulkUnitType && (
                <Typography variant="body2" sx={{ color: '#5a6c7d', mt: 1 }}>
                  Grouping is disabled when any selected item uses a bulk unit type.
                </Typography>
              )}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
              mt: 1,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#f8fbff',
              border: '1px solid #d9e6f7'
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003883' }}>
                Apply units to all selected items
              </Typography>
              <Typography variant="body2" sx={{ color: '#5a6c7d', mt: 0.5 }}>
                Selected items start at 0. Update this field here to apply the same units to all selected items.
              </Typography>
            </Box>
            <TextField
              label="Units"
              type="number"
              size="small"
              value={sharedSelectedUnits}
              onChange={(e) => {
                const value = e.target.value;

                if (value === '') {
                  handleSharedSelectedUnitsChange('');
                } else {
                  handleSharedSelectedUnitsChange(Number(value));
                }
              }}
              onBlur={(e) => handleSharedSelectedUnitsBlur(e.target.value)}
              inputProps={{ min: 0, style: { width: 70 } }}
            />
          </Box>

          <Stack spacing={1.5}>
            {selectedItems.map((item: any) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  border: '1px solid #e0e7ef',
                  borderRadius: 2,
                  backgroundColor: '#fff'
                }}
              >
                {/* Column 1: Item info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#003883' }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#5a6c7d' }}>
                    {activeTab === 0
                      ? (item.volume !== 0 ? `${item.volume} ${item.volumeUnit}` : '-')
                      : (item.size === 0 || !item.size ? '-' : item.size)}
                  </Typography>
                  {activeTab === 0 && (
                    <Typography variant="body2" sx={{ color: '#5a6c7d', mt: 0.5 }}>
                      Unit Type: {getItemUnitType(item)}
                    </Typography>
                  )}
                </Box>
                {/* Column 2: Multiplier */}
                {!["Quality", "Packing"].includes(item.group) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ color: '#5a6c7d', fontWeight: 500 }}>Multiplier</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, overflow: 'hidden', height: 32 }}>
                      <IconButton
                        size="small"
                        disabled={getItemUnitType(item) !== 'Individual'}
                        onClick={() => handleMultiplierChange(item.id, Math.max(1, getMultiplierValue(item.id) - 1))}
                        sx={{ borderRadius: 0, height: '100%', width: 26, p: 0, minWidth: 0 }}
                      >
                        <RemoveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <input
                        type="number"
                        value={getMultiplierValue(item.id)}
                        disabled={getItemUnitType(item) !== 'Individual'}
                        min={1}
                        max={6}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 1 && val <= 6) handleMultiplierChange(item.id, val);
                        }}
                        style={{
                          width: 32,
                          textAlign: 'center',
                          border: 'none',
                          borderLeft: '1px solid rgba(0,0,0,0.12)',
                          borderRight: '1px solid rgba(0,0,0,0.12)',
                          outline: 'none',
                          fontSize: '12px',
                          height: '100%',
                          padding: 0,
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield',
                        } as React.CSSProperties}
                      />
                      <IconButton
                        size="small"
                        disabled={getItemUnitType(item) !== 'Individual'}
                        onClick={() => handleMultiplierChange(item.id, Math.min(6, getMultiplierValue(item.id) + 1))}
                        sx={{ borderRadius: 0, height: '100%', width: 26, p: 0, minWidth: 0 }}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>)}
                {/* Column 3: Units */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: '#5a6c7d', fontWeight: 500 }}>Units</Typography>
                  <Chip
                    label={getQuantityValue(item.id)}
                    sx={{ backgroundColor: '#eaf2ff', color: '#003883', fontWeight: 600, minWidth: 48 }}
                  />
                </Box>
                {/* Column 4: Delete */}
                <Tooltip title="Remove item" arrow>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => toggleSelectedItem(item)}
                    sx={{ flexShrink: 0 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseReviewPopup}
              sx={{ fontWeight: 600 }}
            >
              Close
            </Button>
            <Tooltip
              title={isClientSelectionRequired ? 'Please select client to proceed for order' : ''}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleAddSelectedItemsToCart}
                  disabled={isClientSelectionRequired || selectedItems.length === 0 || hasSelectedItemsWithZeroQuantity || isGroupingNameInvalid}
                  sx={{ fontWeight: 600 }}
                >
                  Add Selected Items To Cart
                </Button>
              </span>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedBottle([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            pb: 2,
            mb: 2,
            borderBottom: '1px solid #e0e0e0',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#003883'
          }}
        >
          Bottle Details
          <Button
            aria-label="close"
            onClick={() => setDetailsOpen(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              minWidth: 0,
              width: 32,
              height: 32,
              padding: 0,
              color: '#5a6c7d',
              fontSize: 24,
              lineHeight: 1,
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            ×
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {selectedBottle && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    padding: 2,
                    '&:hover .zoom-btn': { opacity: 1, pointerEvents: 'auto' }
                  }}
                >
                  {((selectedBottleTab === 0 && hasBottleImage(selectedBottle)) || (selectedBottleTab === 1 && hasQualityPackingImage(selectedBottle))) ? (
                    <img
                      src={selectedBottleTab === 0 ? getBottleImageUrl(selectedBottle) : getQualityPackingImageUrl(selectedBottle)}
                      alt={selectedBottle.name}
                      style={{
                        width: '100%',
                        borderRadius: 8,
                        objectFit: 'contain',
                        transform: detailsImageZoomed ? 'scale(1.8)' : 'scale(1)',
                        transition: 'transform 0.2s ease-in-out'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 2,
                        backgroundColor: '#fff'
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                        {selectedBottle.name}
                      </Typography>
                    </Box>
                  )}
                  {((selectedBottleTab === 0 && hasBottleImage(selectedBottle)) || (selectedBottleTab === 1 && hasQualityPackingImage(selectedBottle))) && (
                    <IconButton
                      className="zoom-btn"
                      onClick={() => setDetailsImageZoomed(prev => !prev)}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(255,255,255,0.85)',
                        opacity: 0,
                        pointerEvents: 'none'
                      }}
                      aria-label="Zoom image"
                    >
                      {detailsImageZoomed ? <ZoomOutIcon /> : <ZoomInIcon />}
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(0, 1fr) 228px',
                  },
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: 'start',
                }}
              >
                {/* LEFT COLUMN: Bottle details */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontSize: '16px',
                      lineHeight: 1.4,
                      fontWeight: 600,
                      color: '#003883',
                    }}
                  >
                    {selectedBottle.name}
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#003883', mb: 0.5, fontSize: 12 }}
                    >
                      Description:
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: '#2c3e50', lineHeight: 1.6, fontSize: 12 }}
                    >
                      {selectedBottleTab === 0
                        ? selectedBottle.description
                        : selectedBottle.comments}
                    </Typography>
                  </Box>

                  {selectedBottleTab === 0 && (
                    <>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#003883',
                            mb: 0.5,
                            mt: 1,
                            fontSize: 12,
                          }}
                        >
                          Preservation Type:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: '#2c3e50', lineHeight: 1.6, fontSize: 12 }}
                        >
                          {selectedBottle.preservationType || 'None'}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#003883',
                            mt: 1,
                            fontSize: 12,
                          }}
                        >
                          Typical Analysis:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: '#2c3e50', lineHeight: 1.6, fontSize: 12 }}
                        >
                          {selectedBottle.analysisType}
                        </Typography>
                      </Box>

                      {/* HOLDING TIME TABLE */}
                      {/* <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#003883',
                            mb: 0.5,
                            fontSize: 12,
                          }}
                        >
                          Hold Time:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#2c3e50',
                            lineHeight: 1.4,
                            mb: 1,
                            fontSize: 12,
                          }}
                        >
                          Typical holding time for analysis conducted on this container is 7 days
                        </Typography>

                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{
                            width: 'fit-content',
                            maxWidth: '100%',
                            borderRadius: 0,
                            overflowX: 'auto',
                          }}
                        >
                          <Table
                            size="small"
                            sx={{
                              width: 'auto',
                              borderCollapse: 'collapse',
                              '& th, & td': {
                                border: '1px solid #000',
                                padding: '8px',
                                fontSize: 12,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                            <TableHead sx={{ backgroundColor: '#003883' }}>
                              <TableRow>
                                <TableCell sx={{ color: '#fff' }}>Analyte</TableCell>
                                <TableCell sx={{ color: '#fff' }}>Holding Time</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {[
                                { analyte: 'pH', time: '2 Days' },
                                { analyte: 'SS', time: '7 Day' },
                                { analyte: 'TDS', time: '7 Days' },
                                { analyte: 'EC', time: '14 Days' },
                                { analyte: 'Alkalinity', time: '2 Days' },
                                { analyte: 'NO3', time: '1 Day' },
                              ].map((item) => (
                                <TableRow key={item.analyte}>
                                  <TableCell>{item.analyte}</TableCell>
                                  <TableCell>{item.time}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box> */}
                    </>
                  )}
                </Box>

                {/* RIGHT COLUMN: Quantity and cart controls */}
                <Box sx={{ minWidth: 0, pt: { xs: 0, sm: '48px' } }}>
                  {selectedBottle?.color && (
                    <Box
                      sx={{
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: '#5a6c7d', fontSize: 12 }}
                      >
                        Color:
                      </Typography>

                      {selectedBottle.color.split('/').map((col: string, idx: number) => {
                        const colorLabel = col.split('-')[0].trim();
                        const hex = colorNameToHex(colorLabel);

                        return (
                          <Box
                            key={idx}
                            component="span"
                            sx={{
                              backgroundColor: hex,
                              color: getContrastColor(hex),
                              px: 1.5,
                              py: 0.6,
                              borderRadius: 5,
                              fontSize: 12,
                              fontWeight: 500,
                              display: 'inline-block',
                            }}
                          >
                            {col.trim()}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: '#5a6c7d',
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      Multiplier:
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 1,
                        overflow: 'hidden',
                        height: 36,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMultiplierChange(
                            selectedBottle.id,
                            Math.max(1, getMultiplierValue(selectedBottle.id) - 1)
                          )
                        }
                        sx={{ borderRadius: 0, height: '100%', width: 30, p: 0 }}
                      >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </IconButton>

                      <input
                        type="number"
                        aria-label="Multiplier"
                        value={getMultiplierValue(selectedBottle.id)}
                        min={1}
                        max={6}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 1 && val <= 6) {
                            handleMultiplierChange(selectedBottle.id, val);
                          }
                        }}
                        style={{
                          width: 40,
                          textAlign: 'center',
                          border: 'none',
                          borderLeft: '1px solid rgba(0,0,0,0.12)',
                          borderRight: '1px solid rgba(0,0,0,0.12)',
                          outline: 'none',
                          fontSize: 14,
                          height: '100%',
                          padding: 0,
                          boxSizing: 'border-box',
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield',
                        } as React.CSSProperties}
                      />

                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMultiplierChange(
                            selectedBottle.id,
                            Math.min(6, getMultiplierValue(selectedBottle.id) + 1)
                          )
                        }
                        sx={{ borderRadius: 0, height: '100%', width: 30, p: 0 }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        sx={{
                          color: '#5a6c7d',
                          fontWeight: 500,
                          backgroundColor: '#f0f4f8',
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          flex: 1,
                          minWidth: 0,
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedBottleTab === 0
                          ? `${selectedBottle.volume} ${selectedBottle.volumeUnit}`
                          : selectedBottle.size === 0 || !selectedBottle.size
                            ? '-'
                            : selectedBottle.size}
                      </Typography>

                      <TextField
                        label="Units"
                        type="number"
                        size="small"
                        value={units}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleDialogUnitsChange(value === '' ? '' : Number(value));
                        }}
                        onBlur={(e) => {
                          const value = Number(e.target.value);
                          if (Number.isNaN(value) || value < 0) {
                            handleDialogUnitsChange(0);
                          }
                        }}
                        inputProps={{ min: 0 }}
                        sx={{
                          width: 88,
                          '& .MuiInputBase-input': { fontSize: 12 },
                          '& .MuiInputLabel-root': { fontSize: 12 },
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#003883' },
                            '&.Mui-focused fieldset': { borderColor: '#003883' },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {selectedBottleTab === 0 && (
                    <Box sx={{ mb: 2 }}>
                      {renderUnitTypeField(selectedBottle)}
                    </Box>
                  )}

                  {selectedBottle?.isContainer !== false && (
                    <Button
                      variant="contained"
                      size="medium"
                      fullWidth
                      sx={{
                        py: 1,
                        fontWeight: 600,
                        fontSize: 12,
                        borderRadius: 5,
                        minHeight: 38,
                        boxShadow: 'none',
                        mb: 2,
                      }}
                      onClick={() => handleAddToCart(selectedBottle)}
                      disabled={
                        isClientSelectionRequired ||
                        getQuantityValue(selectedBottle.id) <= 0
                      }
                    >
                      Add To Cart
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Demo Modal */}
      <DemoModal
        open={demoModalOpen}
        onClose={handleCloseDemoModal}
        dontShowAgain={dontShowDemoAgain}
        onDontShowAgainChange={handleDontShowDemoAgain}
        steps={demoSteps}
        title="Bottle Order Walkthrough"
      />
    </div>
  );
}
